import React, { useContext, useMemo, useState } from "react";
import { Timestamp } from "firebase/firestore";
import { useBetContext } from "../../../../context/BetContext.tsx";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { BetDetails } from "../../../../models/MyBetModel.tsx";
import { BetSelectorCommercialLive } from "./BetSelectorCommercialLive.tsx";
import { useUser } from "../../../../context/UserContext.tsx";
import SportContext, { useSport } from "../../../../context/SportContext.tsx";
import { ALLOWED_MARKETS, calcProfit, calcStake, extractPeriod, filterPreferredSportsbookOdds, formatMarketTitle, getName, PeriodTeamSuffixLabel, preferPrimarySportsbookOdds } from "../../../../helpers/functions.tsx";
import ImageWithPlaceholder from "../../../ImageWithPlaceholder.tsx";
import { DEFAULT_PROPS_LOGO, logoForSelectionLine } from "../../modals/BetLogo.tsx";
import { SlotConfig } from "../../../../models/Sport.tsx";
import { lockPageScroll } from "../../../../helpers/scrollPageToTop.ts";

interface CommercialLiveTeamProps {
    gameId: string;
    sport: string;
    team: string;
    odds: Odd[];
    league: string;
    onShowBetsModal: Function;
    gameTime: Date;
    gameLogo?: string;
    opposingTeam: string;
    fixture_id: string;
    showFlag?: boolean;
    is_live: boolean;
    setLoadingBet: React.Dispatch<React.SetStateAction<boolean>>;
    loadingBet: boolean;
    clock: string;
    isAvailable: boolean;
    logoLeague?: string;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
    teamScore?: number | null;
}

export function CommercialLiveTeam(props: CommercialLiveTeamProps) {

    const { showFlag, gameId, sport, team, odds, league, gameLogo, opposingTeam, fixture_id, is_live, isAvailable, onShowBetsModal, setErrorMessage, teamScore } = props;
    const { bets, upsertBet } = useBetContext();
    const { marketOrderFromDB } = useSport();
    const { user } = useUser();
    if (!user?.userName) {
        throw new Error("User not authenticated");
    }
    const gameTime = Timestamp.fromDate(props.gameTime)
    const marketTitle = formatMarketTitle(odds?.[0]?.market, league);
    const rawName = odds?.[0]?.market || "";
    const period = extractPeriod(rawName);
    const shouldShowMarketTitle = !!period && ALLOWED_MARKETS.has(period);

    const _build = (reward: number, selected: Odd): BetDetails => {
        const price = selected?.price;
        let risk = 0;
        let finalReward = 0;

        if (price > 0) {
            risk = reward;
            finalReward = calcProfit(risk, price);
        } else {
            finalReward = reward;
            risk = calcStake(finalReward, price);
        }

        let selectionType: string | undefined;
        if (selected.market === "Moneyline") {
            selectionType = "ML";
        } else if (selected.selection_line) {
            const line = selected.selection_line.toLowerCase();
            if (line === "over") {
                selectionType = "O";
            } else if (line === "under") {
                selectionType = "U";
            } else {
                selectionType = selected.selection_line;
            }
        } else {
            selectionType = selected.selection_line;
        }

        const logoToShow = logoForSelectionLine(selected.selection_line, gameLogo || "");
        let marketDisplay = ''
        if (shouldShowMarketTitle) {
            marketDisplay = formatMarketTitle(selected.market, league);
        }

        return {
            fixture_id,
            id_selection: selected.id,
            bet: risk,
            reward: finalReward,
            sport,
            league,
            gameDate: gameTime,
            gameId,
            marketName: selected.market,
            betName: selected.name,
            teamId: team,
            betDate: Timestamp.now(),
            betStatus: 'pending',
            betType: 'Straight',
            gameType: 'live',
            odd: selected,
            opposingTeam,
            logo: logoToShow,
            marketType: marketDisplay,
            ...(selectionType ? { selectionType } : {}),
        };
    };

    const onRequestBetsModal = (update?: Partial<BetDetails>, openModal: boolean = false) => {
        if (!is_live || !isAvailable) {
            return;
        }
        if (user.status !== "Active") {
            setErrorMessage("Your account is in read-only mode."
            );
            return;
        }
        const reward = update?.reward ?? 0;
        const odd = update?.odd;

        if (!odd) return;
        if (!openModal && !reward) return;

        if (openModal) lockPageScroll();

        const newBet = _build(reward, odd);
        const updatedBets = (() => {
            const exists = bets.find(b => b.id_selection === newBet.id_selection);

            if (exists) {
                return bets.map(b =>
                    b.id_selection === newBet.id_selection ? newBet : b
                );
            }

            return [...bets, newBet];
        })();

        upsertBet(newBet);

        if (openModal) {
            onShowBetsModal(updatedBets);
        }
    };

    const oddsMatrix = useMemo(() => {
        if (odds.length === 0) {
            return [];
        }

        const preferredOdds = preferPrimarySportsbookOdds(odds);

        const moneylineOdds = preferredOdds.filter(
            o => o.market_id?.toLowerCase() === "moneyline" || o.market?.toLowerCase() === "moneyline"
        );

        if (marketOrderFromDB.length === 0) {
            return moneylineOdds.length > 0 ? [[], moneylineOdds] : [];
        }

        const oddsByMarket = Object.groupBy(preferredOdds, odd => odd.market_id);
        const matrix = [...marketOrderFromDB]
            .sort((a: SlotConfig, b: SlotConfig) => a.slot - b.slot)
            .map((slot: SlotConfig) =>
                slot.marketIds.flatMap((marketId: string) => oddsByMarket[marketId] ?? [])
            );

        const assignedIds = new Set(matrix.flat().map(o => o.id));
        const orphanMoneyline = moneylineOdds.filter(o => !assignedIds.has(o.id));
        if (orphanMoneyline.length === 0) {
            return matrix;
        }

        const configuredMlSlot = marketOrderFromDB.findIndex(slot =>
            slot.marketIds.some(id => id.toLowerCase() === "moneyline")
        );
        const mlSlotIndex = configuredMlSlot >= 0 ? configuredMlSlot : 1;
        const next = [...matrix];
        while (next.length <= mlSlotIndex) next.push([]);
        next[mlSlotIndex] = [...next[mlSlotIndex], ...orphanMoneyline];
        return next;
    }, [odds, marketOrderFromDB]);

    const leagueKey = String(league ?? "").toUpperCase();
    const sportKey = String(sport ?? "").toUpperCase();
    const isMoneylineOnly =
        leagueKey === "UFC" ||
        leagueKey === "ATP" ||
        leagueKey === "WTA" ||
        sportKey === "TENNIS";

    const slotsToRender = () => {
        // Match Bootstrap `lg` (992px) used by d-lg-flex / d-none in the market columns
        const isSmallScreen = window.innerWidth < 992;

        let slots: Odd[][];

        if (isSmallScreen) {
            const firstSlot = oddsMatrix[0]?.length === 0 ? oddsMatrix[1] : oddsMatrix[0];
            const secondSlot = oddsMatrix[0]?.length === 0 ? oddsMatrix[0] : oddsMatrix[1];

            slots = [
                firstSlot ?? [],
                secondSlot ?? [],
                oddsMatrix[2] ?? [],
                oddsMatrix[3] ?? [],
            ];
        } else {
            slots = [
                oddsMatrix[0] ?? [],
                oddsMatrix[1] ?? [],
                oddsMatrix[2] ?? [],
                oddsMatrix[3] ?? [],
            ];
        }

        if (isMoneylineOnly) {
            const moneylineOdds =
                (oddsMatrix[1]?.length ? oddsMatrix[1] : null) ||
                (oddsMatrix[0]?.length ? oddsMatrix[0] : null) ||
                filterPreferredSportsbookOdds(odds.filter(o =>
                    o.market_id?.toLowerCase() === "moneyline" ||
                    o.market?.toLowerCase() === "moneyline"
                ));

            if (isSmallScreen) {
                // market-0 is always visible on small screens
                slots[0] = moneylineOdds;
                slots[1] = [];
            } else {
                // market-1 aligns with MONEYLINE header on desktop
                slots[0] = [];
                slots[1] = moneylineOdds;
            }
        }

        return slots;
    };
    const renderedSlots = slotsToRender();
    const shouldRenderMarkets = is_live && isAvailable;
    const displayName = getName(team, league);

    const renderMarkets = () =>
        renderedSlots.map((slotOdds, i) => {
            const marketId = slotOdds.at(0)?.market_id ?? `empty-${i}`;
            // Ensure moneyline-only odds in slot 1 stay visible (desktop MONEYLINE column)
            const forceShow = isMoneylineOnly && i === 1 && slotOdds.length > 0;
            // Tennis/UFC have no spread, so ML uses slotIndex 1 (price only).
            // Totals still need slot 2/3 so the line (+21.5) renders like MLB.
            const displaySlotIndex = isMoneylineOnly && i < 2 ? 1 : i;

            return (
                <div
                    key={i}
                    className={`market-${i} ${forceShow || i === 0 || i === 2 ? "" : "d-none"} d-lg-flex market-center main-markets market-${marketId}`}
                >
                    {slotOdds.length > 0 ? (
                        <BetSelectorCommercialLive
                            availableOdds={slotOdds}
                            onRequestBetsModal={onRequestBetsModal}
                            slotIndex={displaySlotIndex}
                        />
                    ) : (
                        <div className={`empty-market market-${marketId}`} />
                    )}
                </div>
            );
        });

    return (
        <>
            <div className="line-container">
                <div className={`team-name-container commercial-live-team ${showFlag ? "single-flag" : ""}`}>
                    <div className="commercial-live-team-main">
                        <ImageWithPlaceholder
                            src={gameLogo || ""}
                            className="sport-logos"
                            alt=""
                            placeholderClassName="placeholder-logos-team"
                            fallbackSrc={DEFAULT_PROPS_LOGO}
                        />
                        <span className="teko team-name name-small" title={team}>{displayName}<PeriodTeamSuffixLabel market={rawName} league={league} /></span>
                        <span className="teko team-name name-big" title={team}>
                            {displayName}<PeriodTeamSuffixLabel market={rawName} league={league} />
                        </span>
                    </div>
                    {is_live && (
                        <span className="commercial-live-team-score teko" aria-label="Score">
                            {teamScore ?? "–"}
                        </span>
                    )}
                </div>
                {shouldRenderMarkets && renderMarkets()}
            </div>
        </>
    )
}
